import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Lock, RefreshCw, Sparkles, HelpCircle, Mic, CalendarClock, Phone, Link as LinkIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import OrbitConversation from './OrbitConversation';
import { aaiApi } from '../../services/aaiApi';
import type { OnboardingState, OnboardingQuestion, OnboardingAnswer } from '../../types/aai';
import { Loading, ErrorNote, StagePill } from './components';
import { DeckCard } from '../../components/orbit-deck/DeckCard';
import '../../components/orbit-deck/deck.css';
import type { DeckAnswer, DeckCardModel } from '../../components/orbit-deck/types';
import { ingestFile, transcribeAudio } from '../../orbit/lib/intakeApi';

// The participant's ~5-minute onboarding. PRIMARY surface is a natural spoken
// conversation with Orbit (ElevenLabs Conversational AI, in-browser). When voice
// isn't available — no mic, denied permission, or the agent isn't configured —
// it falls back to a plain TEXT flow (type your answer). There is no longer a
// tap-chips / push-to-talk surface; voice or text, nothing in between.

const micSupported =
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

// Coerce a typed answer into the structured value the scorer expects — mirrors
// the coercion the voice client tool does, so text and voice store the same shape.
function coerceTyped(raw: string, q: OnboardingQuestion): OnboardingAnswer {
  const text = raw.trim();
  const opts = q.options || [];
  const matchOne = (x: string): string => {
    const s = x.trim().toLowerCase();
    const m = opts.find((o) => o.value.toLowerCase() === s || (o.label || '').toLowerCase() === s);
    return m ? m.value : x.trim();
  };
  if (q.type === 'scale') {
    const n = parseInt(text.match(/[1-5]/)?.[0] ?? '', 10);
    return Number.isNaN(n) ? text : n;
  }
  if (q.type === 'multi') return text.split(/[,;]+/).map(matchOne).filter(Boolean);
  if (q.type === 'single' || q.type === 'confirm') return matchOne(text);
  return text;
}

// `datetime-local` reads and writes local wall-clock time, so toISOString (UTC)
// would shift the value by the viewer's offset. Default: tomorrow, 9am.
function localInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// The onboarding question as a deck card: the same anatomy the owner's deck
// and the Room use, so onboarding never reads as a form.
function cardFor(q: OnboardingQuestion, phaseTitle: string): DeckCardModel {
  let input: DeckCardModel['input'] = 'say';
  let options: { value: string; label: string }[] = [];
  if (q.type === 'scale') {
    input = 'choose';
    const min = q.scale?.min_label ?? 'low';
    const max = q.scale?.max_label ?? 'high';
    options = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: n === 1 ? `1 · ${min}` : n === 5 ? `5 · ${max}` : String(n) }));
  } else if (q.type === 'multi') {
    input = 'choose_many';
    options = (q.options || []).map((o) => ({ value: o.value, label: o.label || o.value }));
  } else if (q.type === 'single' || q.type === 'confirm') {
    input = 'choose';
    options = (q.options || []).map((o) => ({ value: o.value, label: o.label || o.value }));
  }
  const handed = (q as unknown as { handed_by?: string }).handed_by;
  let tagLabel = phaseTitle;
  if (handed) tagLabel = 'Handed to you';
  if (q.locked) tagLabel = 'Same for everyone';
  return {
    id: q.id, tag: q.type === 'confirm' ? 'confirm' : 'ask', tagLabel, title: q.say, why: q.why || '', input, options,
    placeholder: q.open?.placeholder || 'Plain words are enough.', prefill: (q as unknown as { prefill?: string }).prefill ? String((q as unknown as { prefill?: string }).prefill) : '',
  };
}

function defaultBookingTime(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return localInputValue(d);
}

const OnboardingFlow: React.FC<{ onCompleted?: () => void }> = ({ onCompleted }) => {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Voice is primary whenever a mic exists; falling back flips this off.
  const [convMode, setConvMode] = useState<boolean>(micSupported);
  const [voiceHint, setVoiceHint] = useState<string | null>(null);
  const [, setOpenText] = useState('');
  // ONB-2: the payoff the just-answered step unlocked, shown inline with the
  // next question — so onboarding never reads as an HR form.
  const [payoff, setPayoff] = useState<string | null>(null);
  // Why the last answer was NOT accepted (re-asked question), from the backend.
  const [note, setNote] = useState<string | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [when, setWhen] = useState<string>(defaultBookingTime);
  const [phone, setPhone] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await aaiApi.getOnboardingState();
      if (data.suggested_phone) setPhone((p) => p || data.suggested_phone!);
      setState(data);
    } catch {
      setError('Could not load your onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // When the question changes, seed the box with any invite-context PREFILL so
  // the person confirms a known fact ("Manager") instead of a blank box. The
  // value is shown as its option label when it maps to one.
  const qid = state?.question?.id;
  useEffect(() => {
    const q: any = state?.question;
    const pv = q?.prefill;
    if (pv == null || pv === '') { setOpenText(''); return; }
    const opt = (q?.options || []).find((o: any) => o.value === pv || o.label === pv);
    setOpenText(opt ? (opt.label || opt.value) : String(pv));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  const submit = useCallback(async (value: OnboardingAnswer, questionId?: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setVoiceHint(null);
    try {
      const { data } = await aaiApi.submitTurn({ question_id: questionId, value });
      setState(data);
      // The backend sets `payoff` only when the answer was ACCEPTED and the flow
      // moved on — a re-presented question (empty required answer, unknown
      // department) carries none, so no stale ✓ above the question being re-asked.
      setPayoff(data.payoff || null);
      setNote(data.note || null);
      if (data.done) onCompleted?.();
    } catch {
      setError('Could not save your answer. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [busy, onCompleted, state]);

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      await aaiApi.acceptConsent();
      const { data } = await aaiApi.getOnboardingState();
      setState(data);
    } catch {
      setError('Could not record your acceptance. Please try again.');
    } finally { setBusy(false); }
  };

  const restart = async () => {
    setBusy(true); setError(null);
    setWelcomeDismissed(false);
    setPayoff(null);
    setNote(null);
    setConvMode(micSupported);
    try { const { data } = await aaiApi.restartOnboarding(); setState(data); }
    catch { setError('Could not start a new check-in. Please try again.'); }
    finally { setBusy(false); }
  };

  const bookCall = async () => {
    if (!phone.trim()) { setError('Add a number Orbit can call.'); return; }
    setBusy(true);
    setError(null);
    try {
      // The input is local wall-clock; Date parses it as local, toISOString
      // hands the server the unambiguous instant.
      const { data } = await aaiApi.scheduleOnboardingCall(
        new Date(when).toISOString(), phone.trim(),
      );
      setState((prev) => (prev ? {
        ...prev,
        call_booking: {
          call_id: data.call_id, scheduled_for: data.scheduled_for,
          to_number: data.to_number, status: 'scheduled',
        },
      } : prev));
      setPickerOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not book that call.');
    } finally { setBusy(false); }
  };

  const cancelCall = async () => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.cancelOnboardingCall();
      setState((prev) => (prev ? { ...prev, call_booking: null } : prev));
      setPickerOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not cancel that call.');
    } finally { setBusy(false); }
  };

  const bookingForm = (
    <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
      <span className="text-[13px] text-gray-600">Orbit calls me on</span>
      <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
        className="rounded-lg border border-gray-200 px-2 py-1 text-[13px] text-gray-800" />
      <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
        placeholder="+1 555 000 1234" aria-label="Phone number"
        className="w-40 rounded-lg border border-gray-200 px-2 py-1 text-[13px] text-gray-800" />
      <Button variant="brand" onClick={bookCall} disabled={busy}>
        {busy ? 'Booking…' : 'Book the call'}
      </Button>
      <button onClick={() => setPickerOpen(false)}
        className="text-gray-500 hover:text-gray-700 text-[13px] px-1">Never mind</button>
    </div>
  );

  // Offered under the start prompt in both voice and typed modes.
  const scheduleLater = (
    <div className="mt-3 text-center">
      {!pickerOpen && (
        <button onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-[13px]">
          <Phone size={14} /> Have Orbit call me later
        </button>
      )}
      {pickerOpen && bookingForm}
    </div>
  );

  if (loading) return <Loading label="Loading your onboarding…" />;
  if (!state) return <ErrorNote message={error || 'Something went wrong.'} />;

  // ── 1) Consent gate (R6 transparency) ──────────────────────────────────
  if (state.consent_required && state.notice) {
    const n = state.notice;
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={18} className="text-brand-500" />
            <h2 className="text-lg font-semibold text-gray-900">{n.headline}</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">{n.body}</p>
          <div className="space-y-3 mb-5">
            {n.scopes.map((s) => (
              <div key={s.scope} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                <p className="text-sm font-medium text-gray-800">{s.label}</p>
                <p className="text-sm text-gray-600 mt-0.5">{s.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
            <Mic size={13} className="text-brand-500" />
            Orbit has a short spoken conversation with you — or you can type your answers instead.
          </p>
          {error && <div className="mb-3"><ErrorNote message={error} /></div>}
          <Button variant="brand" onClick={accept} disabled={busy}>
            {busy ? 'Saving…' : 'I understand & accept'}
          </Button>
        </div>
      </div>
    );
  }

  // ── 2) Done — recap card ────────────────────────────────────────────────
  if (state.done) {
    const r = state.recap;
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-black text-white rounded-xl p-6 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={20} className="text-emerald-400" />
            <span className="font-bold text-[15px]">That's it — nice work.</span>
          </div>
          {r ? (
            <>
              <p className="text-[13.5px] text-white/70 leading-relaxed mb-4">Here's what I picked up — private to you and your coach, and it only goes up from here.</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <RecapCell label="Role" value={r.role} />
                <RecapCell label="Biggest time sink" value={r.top_time_sink || '—'} />
                <RecapCell label="Task to hand off" value={r.handoff || '—'} />
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-1">Starting readiness</div>
                  <StagePill stage={r.stage || state.readiness?.stage} />
                </div>
              </div>
            </>
          ) : (
            <p className="text-[13.5px] text-white/70 mb-4">Your AI-readiness profile is up to date.</p>
          )}
          <p className="text-[11.5px] text-white/45">Provisional · self-reported, shown as a range.</p>
        </div>
        {error && <div className="mb-3"><ErrorNote message={error} /></div>}
        <Button variant="outline" onClick={restart} disabled={busy}>
          <RefreshCw size={15} className="mr-1.5" />{busy ? 'Starting…' : 'Start a new check-in'}
        </Button>
      </div>
    );
  }

  // ── 2.5) Booked — Orbit rings them at the time they chose ───────────────
  if (state.call_booking) {
    const b = state.call_booking;
    const at = b.scheduled_for ? new Date(b.scheduled_for) : null;
    const ringing = b.status === 'dialing' || b.status === 'in_progress';
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-100 p-7 text-center">
          <span className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-full bg-brand-50 text-brand-600">
            <Phone size={24} />
          </span>
          <p className="text-[15px] font-semibold text-gray-900 mb-1">
            {ringing ? 'Orbit is calling you now' : 'Orbit will call you'}
          </p>
          <p className="text-[13px] text-gray-500 mb-5 max-w-sm mx-auto">
            {ringing
              ? `Pick up on ${b.to_number} — it takes about five minutes.`
              : `${at ? at.toLocaleString() : 'Shortly'} on ${b.to_number}. Orbit asks the same questions, you answer on the phone.`}
          </p>
          {error && <div className="mb-3"><ErrorNote message={error} /></div>}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button variant="brand" onClick={cancelCall} disabled={busy}>
              <Mic size={15} className="mr-1.5" />{busy ? 'Cancelling…' : 'Do it here instead'}
            </Button>
            <button onClick={() => { if (at) setWhen(localInputValue(at)); setPhone(b.to_number || ''); setPickerOpen(true); }}
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-[13px]">
              <CalendarClock size={14} /> Change time or number
            </button>
            <button onClick={cancelCall} disabled={busy}
              className="text-gray-500 hover:text-gray-700 text-[13px]">Cancel the call</button>
          </div>
          {pickerOpen && <div className="mt-4">{bookingForm}</div>}
        </div>
      </div>
    );
  }

  // ── 3) Voice conversation (PRIMARY) ─────────────────────────────────────
  // Drives the same state/turn endpoints; on finish it reloads to show the recap.
  if (convMode) {
    return (
      <div className="max-w-2xl mx-auto">
        <OrbitConversation
          onDone={() => { setConvMode(false); load(); }}
          onExit={(fellBack) => {
            setConvMode(false);
            if (fellBack) setVoiceHint('Live voice isn’t available right now — type your answers here instead.');
          }}
        />
        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
        {scheduleLater}
      </div>
    );
  }

  // ── 4) Text fallback — type your answer, no tapping or push-to-talk ──────
  const q = state.question;
  if (!q) return <ErrorNote message="No question to show." />;
  const ph = q.phase;
  const showWelcome = !!state.welcome && !welcomeDismissed;
  const text = showWelcome ? state.welcome!.voice : q.say;
  const qNum = q.id.split('_')[0].toUpperCase();

  return (
    <div className="max-w-2xl mx-auto">
      {/* phase header + segmented progress */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-600">
          Phase {ph.index} of {ph.total} · {ph.title.toUpperCase()}
        </span>
        <div className="flex gap-1">
          {Array.from({ length: ph.total }).map((_, i) => (
            <div key={i} className={`h-1.5 w-6 rounded-full ${i < ph.index ? 'bg-brand-500' : 'bg-gray-200'}`} />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        {micSupported && !showWelcome && (
          <button onClick={() => { setVoiceHint(null); setConvMode(true); }}
            className="w-full mb-5 inline-flex items-center justify-center gap-2 rounded-lg border border-brand-200 bg-brand-50 text-brand-700 text-[12.5px] font-semibold px-3 py-2 hover:bg-brand-100 transition-colors whitespace-nowrap">
            <Mic size={14} /> Talk to Orbit instead — a natural conversation
          </button>
        )}

        {!showWelcome && payoff && (
          <p className="text-[12.5px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mb-4">
            ✓ {payoff}
          </p>
        )}

        {/* Orbit orb + the welcome (the question itself is the card below) */}
        {showWelcome && (
        <div className="flex items-start gap-3 mb-5">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white">
            <Sparkles size={16} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-wider text-gray-400 mb-1">
              Orbit{!showWelcome && <span className="text-gray-300">· {qNum}</span>}
            </div>
            <p className="text-[16px] text-gray-900 leading-snug">{text}</p>
            {!showWelcome && q.why && (
              <p className="text-[12.5px] text-gray-500 mt-2 flex items-start gap-1.5">
                <HelpCircle size={13} className="mt-0.5 shrink-0 text-gray-400" />
                <span><span className="font-semibold text-gray-600">Why I ask —</span> {q.why}</span>
              </p>
            )}
            {!showWelcome && q.locked && (
              <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                <Lock size={11} /> Same for everyone — keeps readiness comparable.
              </p>
            )}
          </div>
        </div>
        )}

        {error && <div className="mb-3"><ErrorNote message={error} /></div>}
        {voiceHint && <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">{voiceHint}</p>}
        {!showWelcome && note && <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">{note}</p>}

        {showWelcome ? (
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setWelcomeDismissed(true)} disabled={busy}
              className="bg-brand-500 hover:bg-brand-600 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full disabled:opacity-50 transition-colors">
              {state.welcome!.buttons[0] || 'Let’s go'}
            </button>
            <button onClick={() => { setVoiceHint('Profile connect is coming soon — I’ll just ask for now.'); setWelcomeDismissed(true); }} disabled={busy}
              className="inline-flex items-center gap-2 border border-gray-200 bg-white text-gray-700 text-[14px] font-semibold px-5 py-2.5 rounded-full hover:border-brand-300 disabled:opacity-50 transition-colors whitespace-nowrap">
              <LinkIcon size={15} className="text-gray-400" />{state.welcome!.buttons[1] || 'I’ll connect Google or Microsoft instead'}
              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">soon</span>
            </button>
          </div>
        ) : (
          <div className="odeck-frame">
            <div className="odeck-frame-head">
              <span className="fk">The deck<small>{`${Math.max(ph.total - ph.index + 1, 1)} phase${ph.total - ph.index + 1 === 1 ? '' : 's'} left`}</small></span>
              <span className="odeck-count">PHASE <b>{String(ph.index).padStart(2, '0')}</b> · {String(ph.total).padStart(2, '0')}</span>
            </div>
            <div className="odeck-card" style={{ position: 'relative', inset: 'auto' }}>
              <DeckCard
                key={q.id}
                card={cardFor(q, ph.title)}
                index={ph.index - 1}
                total={ph.total}
                busy={busy}
                labels={{ primary: 'Continue', skip: q.required ? 'Skip for now' : 'Skip, Orbit will find out', busy: 'Saving. Orbit is choosing your next question.' }}
                transcribe={(blob) => transcribeAudio(blob)}
                onAnswer={(a: DeckAnswer) => {
                  let value: OnboardingAnswer = a.value as OnboardingAnswer;
                  if (q.type === 'scale') value = parseInt(String(a.value), 10);
                  if (a.how === 'typed' || a.how === 'voice') value = coerceTyped(String(a.value), q);
                  submit(value, q.id);
                }}
                onSkip={() => submit(q.type === 'multi' ? [] : '', q.id)}
                onDocument={async (file) => {
                  try {
                    await ingestFile('doc', file, file.name);
                    setVoiceHint(`${file.name} read. Anything it answered is filled in; the deck moves on.`);
                    load();
                  } catch {
                    setError('Could not read that file.');
                  }
                }}
              />
            </div>
            <div className="odeck-frame-foot"><span>Answer another way, always:</span><span className="ways"><button type="button" onClick={() => { setVoiceHint(null); setConvMode(true); }}>voice</button><i>·</i><button type="button" onClick={() => submit(q.type === 'multi' ? [] : '', q.id)}>skip</button></span></div>
          </div>
        )}
      </div>
      {scheduleLater}
    </div>
  );
};

// ── recap cell ────────────────────────────────────────────────────────────
const RecapCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white/5 rounded-lg p-3 min-w-0">
    <div className="font-sans text-[10px] uppercase tracking-wider text-white/50 mb-1">{label}</div>
    <div className="text-[13px] font-medium truncate">{value}</div>
  </div>
);

export default OnboardingFlow;
